import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PaymentStateScreen from './PaymentStateScreen';
import { classifyPaymentMessage } from '../../utils/paymentState.mjs';
import {
    ConfirmationPage, ConfirmationHero, ConfirmationPanel, ConfirmationCallout, ConfirmationSteps, ConfirmationFooter
} from './PaymentConfirmation';

const GroupPaymentSuccess = () => {
    const { publicId, confirmationNumber } = useParams();
    const [paymentData, setPaymentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchPaymentConfirmation();
    }, [publicId, confirmationNumber]);

    const fetchPaymentConfirmation = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/group-bookings/public/${publicId}/payment-success/${confirmationNumber}`);
            setPaymentData(response.data);
        } catch (error) {
            console.error('Error fetching payment confirmation:', error);
            setError(error.response?.data?.message || 'Failed to load payment confirmation');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return <PaymentStateScreen variant="loading" message="Loading your confirmation..." />;
    }

    if (error) {
        const variant = classifyPaymentMessage(error);
        return (
            <PaymentStateScreen
                variant={variant}
                title={variant === 'error' ? "We Couldn't Load Your Confirmation" : variant === 'not-found' ? "We Couldn't Find Your Confirmation" : undefined}
                message={variant === 'error' ? error : variant === 'not-found' ? "We couldn't find a completed payment for this confirmation number." : undefined}
                onRetry={() => window.location.reload()}
            />
        );
    }

    const money = (value) => parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
        <ConfirmationPage>
            <ConfirmationHero title="Payment Received">
                <p>You've successfully joined the group trip.</p>
            </ConfirmationHero>

            <ConfirmationPanel
                title="Trip Details"
                rows={[
                    { label: 'Trip', value: paymentData.trip.title },
                    { label: 'Destination', value: paymentData.trip.destination },
                    { label: 'Travel Dates', value: `${formatDate(paymentData.trip.start_date)} - ${formatDate(paymentData.trip.end_date)}` },
                    { label: 'Package', value: paymentData.participant.package }
                ]}
            />

            <ConfirmationPanel
                title="Payment Information"
                rows={[
                    { label: 'Confirmation Number', value: paymentData.confirmation_number, tone: 'mono' },
                    { label: 'Amount Paid', value: `$${money(paymentData.participant.amount_paid)}`, tone: 'accent' },
                    { label: 'Traveler', value: paymentData.participant.name },
                    { label: 'Email', value: paymentData.participant.email }
                ]}
            />

            <ConfirmationPanel
                title="Your Travel Agent"
                rows={[
                    { label: 'Agent', value: paymentData.agent.name },
                    { label: 'Agency', value: paymentData.agent.agency },
                    { label: 'Email', value: paymentData.agent.email, href: `mailto:${paymentData.agent.email}` },
                    { label: 'Phone', value: paymentData.agent.phone, href: `tel:${paymentData.agent.phone}` }
                ]}
            />

            <ConfirmationCallout title="What's Next?">
                <ConfirmationSteps
                    items={[
                        { icon: 'mail', content: (<><strong>Confirmation Email:</strong> You'll receive a detailed confirmation email shortly with your booking information and travel documents.</>) },
                        { icon: 'user', content: (<><strong>Agent Contact:</strong> Your travel agent will be in touch with additional details, travel requirements, and group coordination information.</>) },
                        { icon: 'list', content: (<><strong>Travel Preparation:</strong> Keep an eye out for important travel information, packing lists, and group meeting details as your trip approaches.</>) }
                    ]}
                />
            </ConfirmationCallout>

            <div className="pc-actions">
                <a
                    href={`mailto:${paymentData.agent.email}?subject=Group Trip: ${paymentData.trip.title}`}
                    className="pc-btn"
                >
                    Contact Your Agent
                </a>
                <button type="button" onClick={() => window.print()} className="pc-btn is-secondary">
                    Print Confirmation
                </button>
            </div>

            <ConfirmationFooter>
                <a className="pc-link" href="https://tpcrm.io">Return to Homepage</a>
            </ConfirmationFooter>
        </ConfirmationPage>
    );
};

export default GroupPaymentSuccess;
